import { addDoc, collection, deleteDoc, doc, getDoc, updateDoc, getDocs, orderBy,  limit, startAfter, query, runTransaction, setDoc, where} from "firebase/firestore";
import { db } from "./firebase";

const eventCollection = collection(db, "Events");
const BOOKING_COLLECTION = collection(db, "Booking");
const MAIL_COLLECTION = collection(db, "mail");

class EventDataService {
    addEvent = (newEvent) => {
        return addDoc(eventCollection, newEvent);
    }

    updateEvent = (id, updatedEvent) => {
        const eventDoc = doc(db, "Events", id);
        return updateDoc(eventDoc, updatedEvent);
    }

    updateEventFields = (id, rsvpsArray, rsvpUids) => {
        const eventDoc = doc(db, "Events", id);
        // Only update the fields rsvps and rsvpUids
        return updateDoc(eventDoc, {
            rsvps: rsvpsArray,
            rsvpUids: rsvpUids
        });
    }
    /**
     * Apply a change to an event's rsvps / rsvpWaitlist inside a transaction.
     *
     * These arrays live on the event document, so the read-modify-write callers
     * used to do (getEvent, mutate the array in memory, updateDoc the whole array
     * back) loses updates: two members booking seconds apart both build their new
     * array from the same snapshot, and the second write erases the first. The
     * charge happens on a separate call that succeeds either way, which is how
     * members ended up paid-with-no-seat.
     *
     * `mutate` receives the CURRENT arrays, read inside the transaction, and
     * returns whichever it changed. If another writer commits first, Firestore
     * re-runs `mutate` against fresh data, so the change lands on top of theirs
     * instead of over them. Mutations must therefore be written as "upsert this
     * member's entry", never "write this whole array", so replaying them is safe.
     *
     * rsvpUids is derived here so it cannot drift out of step with rsvps.
     */
    applyRsvpChange = (id, mutate) => {
        const eventDoc = doc(db, "Events", id);
        return runTransaction(db, async (tx) => {
            const snap = await tx.get(eventDoc);
            if (!snap.exists()) {
                throw new Error(`Event ${id} no longer exists`);
            }
            const result = mutate({
                rsvps: [...(snap.get("rsvps") || [])],
                rsvpWaitlist: [...(snap.get("rsvpWaitlist") || [])],
            }) || {};
            const update = {};
            if (result.rsvps) {
                update.rsvps = result.rsvps;
                update.rsvpUids = result.rsvps
                    .filter((r) => r && (r.isAttending === true || r.isAttending === "true"))
                    .map(({ userId }) => userId);
            }
            if (result.rsvpWaitlist) {
                update.rsvpWaitlist = result.rsvpWaitlist;
            }
            if (Object.keys(update).length > 0) {
                tx.update(eventDoc, update);
            }
            return result;
        });
    }

    addWailitstMemberEventFields = (id, waitlistArray) => {
        const eventDoc = doc(db, "Events", id);
        // Only update the fields rsvps and rsvpUids
        return updateDoc(eventDoc, {
            rsvpWaitlist: waitlistArray
        });
    }

    deleteEvent = (id) => {
        const eventDoc = doc(db, "Events", id);
        return deleteDoc(eventDoc);
    }

    getAllEvent = () => {
        return getDocs(eventCollection)
    }

    // Function to get events with pagination
    getAllEventsWithPage = async (pageSize, startAfterdt) => {
        let q = query(eventCollection, orderBy("startDate", 'desc')); // Initialize query
        if (startAfterdt) {
            q = query(q, startAfter(startAfterdt)); // Corrected: Use query to chain methods
        }
        if (pageSize) {
            q = query(q, limit(pageSize)); // Corrected: Use query to chain methods
        }
        const snapshot = await getDocs(q);
        const events = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
        const lastVisible = snapshot.docs[snapshot.docs.length - 1];
        return { events, lastVisible };
    };

    getEvent = (id) => {
        const eventDoc = doc(db, "Events", id);
        return getDoc(eventDoc);
    }

    async getBookingByUserAndEvent(bookObjId, createdBy) {
        const bookingQuery = query(
            BOOKING_COLLECTION, 
            where('bookObjId', '==', bookObjId),
            where('createdBy', '==', createdBy)
        );
        const querySnapshot = await getDocs(bookingQuery);
        return querySnapshot;
    }

    // Add a new booking document
    async addNewBooking(bookingData) {
        const bookingRef = doc(BOOKING_COLLECTION);  // Generate a new doc reference with auto ID
        const generatedId = bookingRef.id;           // Extract the auto-generated ID
        await setDoc(bookingRef, { ...bookingData, id: generatedId });
        return generatedId;
    }

    // Update an existing booking document
    async updateExistingBooking(bookingDocId, bookingData) {
        const bookingRef = doc(db, "Booking", bookingDocId);
        await updateDoc(bookingRef, bookingData);
        return bookingDocId;
    }
    
    async sendEmail(toEmail, message) {
        try {
            const res = await addDoc(MAIL_COLLECTION, {
                to: toEmail,
                message: message
            });
            return res;
        } catch (error) {
            throw new Error(`Error sending email: ${error.message}`);
        }
    }
}

export default new EventDataService();